import React, { useState, useEffect } from 'react';
import { productsApi, unitsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Package,
  Loader2
} from 'lucide-react';

const ProductsPage = () => {
  const { isOperator, isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    unit_id: '',
    stock_min_global: 0
  });
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ total: 0 });

  const fetchProducts = async (query = '') => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (query) params.q = query;
      
      const response = await productsApi.list(params);
      setProducts(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await unitsApi.list();
      setUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchUnits();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(searchQuery);
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData({
      sku: '',
      name: '',
      description: '',
      unit_id: units[0]?.id || '',
      stock_min_global: 0
    });
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku,
      name: product.name,
      description: product.description || '',
      unit_id: product.unit_id,
      stock_min_global: product.stock_min_global
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingProduct) {
        await productsApi.update(editingProduct.id, {
          name: formData.name,
          description: formData.description,
          unit_id: formData.unit_id,
          stock_min_global: parseFloat(formData.stock_min_global)
        });
        toast.success('Producto actualizado');
      } else {
        await productsApi.create({
          ...formData,
          stock_min_global: parseFloat(formData.stock_min_global)
        });
        toast.success('Producto creado');
      }
      setDialogOpen(false);
      fetchProducts(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`¿Eliminar el producto "${product.name}"?`)) return;

    try {
      await productsApi.delete(product.id);
      toast.success('Producto eliminado');
      fetchProducts(searchQuery);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.symbol || unit?.code || '';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Productos</h1>
          <p className="text-slate-500 mt-1">Gestiona el catálogo de productos</p>
        </div>
        {isOperator() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="create-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por SKU o nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
            <Button type="submit" variant="secondary">
              Buscar
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" />
            Lista de Productos
            <Badge variant="secondary" className="ml-2">{meta.total}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No se encontraron productos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">SKU</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Nombre</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Unidad</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Costo Prom.</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Stock Mín.</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Estado</th>
                    {isOperator() && (
                      <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="text-sm font-mono font-medium text-blue-900">{product.sku}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-slate-900">{product.name}</p>
                        {product.description && (
                          <p className="text-xs text-slate-500 truncate max-w-xs">{product.description}</p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{getUnitName(product.unit_id)}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right tabular-nums">
                        ${product.average_cost.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right tabular-nums">
                        {product.stock_min_global}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={product.is_active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-slate-100 text-slate-600'
                        }>
                          {product.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      {isOperator() && (
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(product)}
                              data-testid={`edit-product-${product.sku}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {isAdmin() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(product)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sku">SKU</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                disabled={!!editingProduct}
                required
                placeholder="PROD-001"
                data-testid="product-sku-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Nombre del producto"
                data-testid="product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit">Unidad</Label>
                <Select
                  value={formData.unit_id}
                  onValueChange={(value) => setFormData({ ...formData, unit_id: value })}
                >
                  <SelectTrigger data-testid="product-unit-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.name} ({unit.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stock_min">Stock Mínimo</Label>
                <Input
                  id="stock_min"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.stock_min_global}
                  onChange={(e) => setFormData({ ...formData, stock_min_global: e.target.value })}
                  data-testid="product-stock-min-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-900 hover:bg-blue-800"
                disabled={saving}
                data-testid="product-submit-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingProduct ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
